import { useMemo, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { queryClient } from '@/app/QueryClientProvider';
import { ConstructorApi } from '../api';
import { usePopups } from '@/hooks';

export const useQuestionnaire = (id) => {
  const { showErrorToast, showSuccessToast } = usePopups();
  const { isLoading, isError, data, error } = useQuery({
    queryKey: [adminLocalizationKeys.getAllQuestionnaires],
    queryFn: adminLocalizationApi.getAllQuestionnaires,
    select: (res) => res.data
  });

  const [isUpdating, seIstUpdating] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);

  const questionnaire = useMemo(() => {
    if (!data || !data?.length) return undefined;

    return data.find((item) => item.id === id);
  }, [id, data]);

  const handleCreate = useCallback(async (qst) => {
    try {
      seIstUpdating(true);
      const result = await ConstructorApi.createQuestionnaire(qst);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getAllQuestionnaires]
        });
        showSuccessToast('Created');
        return result;
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      seIstUpdating(false);
    }
  }, []);

  const handleUpdate = useCallback(async (qst) => {
    try {
      seIstUpdating(true);
      const result = await ConstructorApi.updateQuestionnaire(qst);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getAllQuestionnaires]
        });
        showSuccessToast('Saved');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      seIstUpdating(false);
    }
  }, []);

  const handleDelete = useCallback(async (qst) => {
    try {
      setIsDeleting(true);
      const result = await ConstructorApi.deleteQuestionnaire(qst);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getAllQuestionnaires]
        });
        showSuccessToast('Deleted');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    data: questionnaire,
    handleCreate,
    handleUpdate,
    handleDelete,
    isLoading,
    isUpdating,
    isDeleting,
    isError,
    error
  };
};
